/**
 * NodeActivity — recent activity timeline (Aether Command aesthetic).
 * Replaces "Yesterday's Summary" slot with a node-style timeline on Dashboard.
 */
import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';
import { GlassCard } from './ui/glass-card';
import { Loader2 } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DOT_COLORS = {
    sales:    '#10B981',
    invoice:  '#A78BFA',
    credit:   '#EF4444',
    stock:    '#F59E0B',
    customer: '#06B6D4',
    payment:  '#62FF96',
    default:  '#FFFFFF',
};

const NodeActivity = () => {
    const { user } = useAuth();
    const { language } = useLanguage();
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => { fetchActivity(); }, []);

    const fetchActivity = async () => {
        try {
            // Try a daily-summary derived activity feed (graceful fallback)
            const res = await axios.get(`${API_URL}/api/analytics/dashboard`, { withCredentials: true });
            const stats = res.data || {};
            const today = new Date();
            const t = (d) => d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
            const derived = [];
            if (stats.today_sales_count) {
                derived.push({
                    id: 'a1', when: `${t(today)} — ${language === 'hi' ? 'बिक्री' : 'SALES'}`,
                    title: language === 'hi' ? `${stats.today_sales_count} नई बिक्रियाँ आज` : `${stats.today_sales_count} new sales today`,
                    detail: language === 'hi' ? `कुल ₹${(stats.today_sales_total||0).toLocaleString('en-IN')} राजस्व` : `₹${(stats.today_sales_total||0).toLocaleString('en-IN')} total revenue`,
                    feature: 'sales',
                });
            }
            if (stats.low_stock_items) {
                derived.push({
                    id: 'a2', when: `${t(today)} — ${language === 'hi' ? 'स्टॉक' : 'STOCK'}`,
                    title: language === 'hi' ? `${stats.low_stock_items} आइटम कम स्टॉक में` : `${stats.low_stock_items} items low on stock`,
                    detail: language === 'hi' ? 'पुनर्भरण की आवश्यकता है' : 'Reorder recommended',
                    feature: 'stock',
                });
            }
            if (stats.credit_given) {
                derived.push({
                    id: 'a3', when: `${t(today)} — ${language === 'hi' ? 'क्रेडिट' : 'CREDIT'}`,
                    title: language === 'hi' ? `₹${stats.credit_given.toLocaleString('en-IN')} बकाया` : `₹${stats.credit_given.toLocaleString('en-IN')} pending`,
                    detail: language === 'hi' ? 'रिमाइंडर भेजने पर विचार करें' : 'Consider sending reminders',
                    feature: 'credit',
                });
            }
            if (stats.total_customers) {
                derived.push({
                    id: 'a4', when: `${language === 'hi' ? 'कल' : 'YESTERDAY'} — ${language === 'hi' ? 'ग्राहक' : 'CUSTOMER'}`,
                    title: language === 'hi' ? `${stats.total_customers} कुल ग्राहक` : `${stats.total_customers} total customers`,
                    detail: language === 'hi' ? 'ग्राहक आधार स्थिर' : 'Customer base steady',
                    feature: 'customer',
                });
            }
            if (derived.length === 0) {
                derived.push({
                    id: 'empty', when: `${t(today)} — ${language === 'hi' ? 'सिस्टम' : 'SYSTEM'}`,
                    title: language === 'hi' ? 'आज की पहली गतिविधि का इंतज़ार' : 'Awaiting your first action today',
                    detail: language === 'hi' ? 'एक बिक्री जोड़कर शुरू करें' : 'Add a sale to get started',
                    feature: 'default',
                });
            }
            setItems(derived);
        } catch (e) { setItems([]); }
        setLoading(false);
    };

    if (!user) return null;

    return (
        <GlassCard hoverable={false} stripe={false} className="p-6 md:p-8">
            <h2 className="headline-md text-white mb-6">
                {language === 'hi' ? 'हाल की गतिविधि' : 'Recent Activity'}
            </h2>

            {loading ? (
                <div className="flex justify-center py-8">
                    <Loader2 className="h-5 w-5 animate-spin text-white/40" />
                </div>
            ) : (
                <div className="relative pl-8 space-y-8">
                    {/* Vertical timeline line */}
                    <div className="absolute left-[11px] top-2 bottom-2 w-px bg-[var(--aether-outline-variant)]" />
                    {items.map((item, idx) => (
                        <div key={item.id} className={`relative ${idx === items.length - 1 && items.length > 1 ? 'opacity-60' : ''}`}>
                            <div
                                className="absolute -left-[27px] top-1 w-2.5 h-2.5 rounded-full"
                                style={{
                                    backgroundColor: DOT_COLORS[item.feature] || DOT_COLORS.default,
                                    boxShadow: '0 0 0 4px #000',
                                }}
                            />
                            <p className="label-caps text-[10px] text-white/55 mb-1">{item.when}</p>
                            <h5 className="text-sm font-bold text-white tracking-tight">{item.title}</h5>
                            <p className="text-xs text-white/55 mt-1">{item.detail}</p>
                        </div>
                    ))}
                </div>
            )}

            <button
                onClick={() => navigate('/analytics')}
                className="mt-10 w-full py-3 border border-dashed border-[var(--aether-outline-variant)] text-white/55 font-bold rounded-lg text-sm hover:border-white hover:text-white transition-all"
                data-testid="view-full-log-btn"
            >
                {language === 'hi' ? 'पूरी रिपोर्ट देखें' : 'View Full Audit Log'}
            </button>
        </GlassCard>
    );
};

export default NodeActivity;
